import { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPause,
  faPlay,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { useTranslations } from 'next-intl';
import type { MediaId } from 'models/Media';
import { useMediaUrl } from 'hooks/useMediaUrl';
import type { UploadStatus } from '../../hooks/useMediaUpload';
import { formatDuration, levelsFromUrl } from './audioLevels';
import { Waveform } from './Waveform';

interface Props {
  // A freshly picked or recorded file, or a stored one.
  previewUrl?: string;
  mediaId?: MediaId;
  status: UploadStatus;
  onRemove: () => void;
}

// One audio clip: play and pause, its waveform with the played part lit,
// its length, and a cross to drop it.
export const AudioClipCard: FC<Props> = ({
  previewUrl,
  mediaId,
  status,
  onRemove,
}) => {
  const t = useTranslations();
  const stored = useMediaUrl(mediaId);
  const src = mediaId ? stored.url : previewUrl;

  const audioRef = useRef<HTMLAudioElement>(null);
  const [levels, setLevels] = useState<number[]>([]);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // The waveform, read from the sound itself.
  useEffect(() => {
    if (!src) {
      return;
    }
    let cancelled = false;
    levelsFromUrl(src).then((next) => {
      if (!cancelled) {
        setLevels(next);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [src]);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  };

  const isUploading = status === 'uploading';
  const isError = status === 'error';
  const shown = isPlaying ? (audioRef.current?.currentTime ?? 0) : duration;

  return (
    <div className={classNames(styles.card, isError && styles.errorCard)}>
      {src && (
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          onError={mediaId ? stored.onError : undefined}
          onLoadedMetadata={(event) => {
            const length = event.currentTarget.duration;
            if (Number.isFinite(length)) {
              setDuration(length);
            }
          }}
          // A fresh recording may report no length until it has played
          // through once; take it from the end then.
          onDurationChange={(event) => {
            const length = event.currentTarget.duration;
            if (Number.isFinite(length)) {
              setDuration(length);
            }
          }}
          onTimeUpdate={(event) => {
            const audio = event.currentTarget;
            if (Number.isFinite(audio.duration) && audio.duration > 0) {
              setProgress(audio.currentTime / audio.duration);
            }
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => {
            setIsPlaying(false);
            setProgress(0);
          }}
        />
      )}

      <button
        type="button"
        onClick={togglePlayback}
        disabled={!src || isError}
        aria-label={
          isPlaying
            ? t('memoComposer.audio.pause')
            : t('memoComposer.audio.play')
        }
        className={styles.play}
      >
        <FontAwesomeIcon
          icon={isError ? faTriangleExclamation : isPlaying ? faPause : faPlay}
          className={classNames(
            'text-[11px]',
            !isPlaying && !isError && 'ml-0.5',
          )}
          aria-hidden="true"
        />
      </button>

      {isError ? (
        <span className={styles.errorText}>
          {t('memoComposer.uploadFailed')}
        </span>
      ) : (
        <Waveform levels={levels} progress={progress} tone="clip" />
      )}

      <span className={styles.time}>
        {isUploading ? (
          <FontAwesomeIcon
            icon={faSpinner}
            className="animate-spin"
            title={t('memoComposer.uploading')}
          />
        ) : (
          formatDuration(shown)
        )}
      </span>

      <button
        type="button"
        onClick={onRemove}
        aria-label={t('memoComposer.audio.remove')}
        className={styles.remove}
      >
        <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
      </button>
    </div>
  );
};

const styles = {
  card: 'flex items-center gap-3 rounded-xl border border-accent-tint-ink/15 bg-accent-tint/50 px-3 py-2',
  errorCard: 'border-mf-red-600/20 bg-mf-red-600/8',
  play: classNames(
    'flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-fill text-white',
    'hover:brightness-110 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-ink/40',
    'disabled:opacity-60',
  ),
  errorText: 'flex-1 text-sm text-mf-red-600',
  time: 'w-10 shrink-0 text-right text-sm tabular-nums text-ink-secondary',
  remove:
    'flex size-7 shrink-0 items-center justify-center rounded-full text-ink-secondary hover:bg-ink-secondary/10 hover:text-ink-primary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-ink/40',
};
